import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, FileImage, Globe, PencilLine, Upload, Trash2, Plus, Star } from "lucide-react";
import { FOOD_CATEGORIES, offeringLabels } from "@/lib/categories";

const CANONICAL = ["category", "name", "description", "price", "package", "image_url", "notes", "availability"];

export default function MenuServiceImport({ vendors }) {
  const [vendorId, setVendorId] = useState(vendors?.[0]?.vendor_id || null);
  const [menus, setMenus] = useState([]);
  const [imports, setImports] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [previewFile, setPreviewFile] = useState(null);

  const activeVendor = vendors?.find(v => v.vendor_id === vendorId) || vendors?.[0];
  const isFood = activeVendor ? FOOD_CATEGORIES.has(activeVendor.category) : true;
  const heading = isFood ? "Import your existing menu" : "Import your packages & services";
  const subHeading = isFood
    ? "No re-typing required"
    : "No re-typing required";
  const helpText = isFood
    ? "Upload what you already have — Excel, CSV, PDF, Word, images or a website URL. Then hit Extract with AI and we'll structure it into a menu automatically. You can also build one manually below."
    : "Upload what you already have — Excel, CSV, PDF, Word, images or a website URL. Then hit Extract with AI and we'll structure your packages and services automatically. You can also enter them manually below.";

  useEffect(() => { if (!vendorId && vendors?.length) setVendorId(vendors[0].vendor_id); }, [vendors, vendorId]);
  const load = () => {
    if (!vendorId) return;
    api.get(`/vendors/${vendorId}/menus`).then(({ data }) => setMenus(data));
    api.get(`/vendors/${vendorId}/menu-imports`).then(({ data }) => setImports(data));
  };
  useEffect(load, [vendorId]);

  const extractImport = async (importId) => {
    toast.info("Extraction started — this runs in the background");
    try {
      await api.post(`/menu-imports/${importId}/extract`);
      // Poll for completion (up to ~2 minutes)
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/menu-imports/${importId}`);
          if (data.status === "processed") {
            toast.success(`Extracted ${data.categories_count || 0} categories`);
            load();
            return;
          }
          if (data.status === "failed") {
            toast.error(`Extraction failed: ${data.error || "unknown error"}`);
            load();
            return;
          }
        } catch { /* transient — keep polling */ }
        if (tries < 60) setTimeout(poll, 2000);
        else { toast.warning("Still working — refresh in a moment"); load(); }
      };
      setTimeout(poll, 1500);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Extraction failed"); }
  };

  const readB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const storeImport = async (file, source) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File exceeds 10MB");
    const b64 = await readB64(file);
    try {
      await api.post(`/vendors/${vendorId}/menu-imports`, { vendor_id: vendorId, source, file_name: file.name, file_type: file.type, file_data_b64: b64 });
      toast.success(source === "pdf" ? "PDF ready for processing" : "Upload stored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openSpreadsheet = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File exceeds 10MB");
    const b64 = await readB64(file);
    try {
      const { data } = await api.post("/imports/spreadsheet-preview", { file_name: file.name, file_data_b64: b64 });
      setPreview(data); setMapping(data.suggested_mapping); setPreviewFile({ name: file.name, type: file.type, b64 });
      setPreviewOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmSpreadsheet = async () => {
    try {
      await api.post(`/vendors/${vendorId}/menu-imports`, {
        vendor_id: vendorId, source: "spreadsheet",
        file_name: previewFile.name, file_type: previewFile.type, file_data_b64: previewFile.b64,
      });
      // Store mapping alongside for future extractor
      toast.success("Spreadsheet queued with your column mapping");
      setPreviewOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const importWebsite = async () => {
    const url = prompt("Enter your website URL (e.g. https://myrestaurant.com):");
    if (!url) return;
    try {
      await api.post(`/vendors/${vendorId}/menu-imports`, { vendor_id: vendorId, source: "website", url });
      toast.success("Import pending"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeImport = async (importId) => {
    if (!window.confirm("Remove this import source? Any already-published menus or packages remain unless deleted separately.")) return;
    try {
      await api.delete(`/menu-imports/${importId}`);
      toast.success("Import source removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete"); }
  };

  if (!vendors?.length) return <div className="text-[color:var(--text-muted)]" data-testid="menu-empty">Create a listing first.</div>;

  return (
    <div data-testid="menu-service-import" className="space-y-6">
      {vendors.length > 1 && (
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger data-testid="menu-vendor-picker" className="bg-white h-11 max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}</SelectContent>
        </Select>
      )}

      {/* Six import options */}
      <div className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
        <div className="eyebrow mb-2">{heading}</div>
        <h2 className="text-2xl font-semibold mb-4">{subHeading}</h2>
        <p className="text-sm text-[color:var(--text-muted)] mb-6">{helpText}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <ImportBtn testid="menu-import-excel" accept=".xlsx,.xls" onFile={openSpreadsheet} icon={FileSpreadsheet} label="Excel" />
          <ImportBtn testid="menu-import-csv" accept=".csv" onFile={openSpreadsheet} icon={FileSpreadsheet} label="CSV" />
          <ImportBtn testid="menu-import-pdf" accept=".pdf" onFile={(f) => storeImport(f, "pdf")} icon={FileText} label="PDF" />
          <ImportBtn testid="menu-import-word" accept=".doc,.docx" onFile={(f) => storeImport(f, "word")} icon={FileText} label="Word" />
          <ImportBtn testid="menu-import-images" accept="image/*" onFile={(f) => storeImport(f, "images")} icon={FileImage} label="Images" multiple />
          <button data-testid="menu-import-website" onClick={importWebsite} className="rounded-lg border border-[color:var(--border-strong)] bg-white p-4 flex flex-col items-center gap-2 hover:bg-[color:var(--surface)] transition-colors">
            <Globe className="w-5 h-5" /><span className="text-xs font-semibold">Website</span>
          </button>
        </div>
      </div>

      {imports.length > 0 && (
        <div className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
          <div className="eyebrow mb-3">Uploaded files</div>
          <div className="space-y-2" data-testid="menu-imports-list">
            {imports.map(i => {
              const showExtract = i.status !== "processed" && i.status !== "processing";
              return (
              <div key={i.import_id} data-testid={`import-row-${i.import_id}`} className="flex items-center justify-between p-3 border border-[color:var(--border)] rounded-lg gap-3">
                <div className="text-sm truncate min-w-0 flex-1">
                  <span className="font-semibold">{i.file_name || i.url}</span>
                  <span className="text-[color:var(--text-muted)] ml-2">· {i.source}</span>
                  {i.menu_id && <span className="text-[10px] uppercase tracking-widest text-[color:var(--verified)] ml-2">→ menu created</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                    i.status === "processed" ? "bg-[color:var(--verified-soft)] text-[color:var(--verified)]" :
                    i.status === "processing" ? "bg-[#FEF3C7] text-[#92400E]" :
                    i.status === "failed" ? "bg-[color:var(--accent)]/10 text-[color:var(--accent)]" :
                    "bg-[color:var(--surface)] text-[color:var(--text-muted)]"
                  }`}>
                    {i.status === "pending_processing" ? "ready to extract" : (i.status || (i.source === "website" ? "pending" : "ready"))}
                  </span>
                  {showExtract && (
                    <Button data-testid={`extract-import-${i.import_id}`} onClick={() => extractImport(i.import_id)}
                      variant="outline" size="sm" className="h-8 text-xs rounded-lg">
                      Extract with AI
                    </Button>
                  )}
                  {i.status !== "processing" && (
                    <button
                      type="button"
                      data-testid={`delete-import-${i.import_id}`}
                      onClick={() => removeImport(i.import_id)}
                      className="text-[color:var(--accent)] hover:opacity-80"
                      title="Remove import source"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {i.error && (
                  <div data-testid={`import-error-${i.import_id}`} className="w-full text-xs text-[color:var(--accent)] mt-1 pl-1">
                    {i.error}
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Manual menu builder */}
      <ManualBuilder vendorId={vendorId} vendorCategory={activeVendor?.category} menus={menus} onChange={load} />

      {/* Spreadsheet mapping dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="mapping-dialog" className="max-w-3xl bg-[color:var(--bg)] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl font-semibold">Adjust column mapping</DialogTitle></DialogHeader>
          {preview && (
            <>
              <p className="text-sm text-[color:var(--text-muted)] mb-4">We auto-detected these columns. Confirm or adjust before importing.</p>
              <div className="space-y-2">
                {preview.headers.map((h, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-40 sm:w-48 text-sm font-semibold truncate">{h || `Column ${idx+1}`}</div>
                    <Select value={mapping[h] || ""} onValueChange={(v) => setMapping({...mapping, [h]: v})}>
                      <SelectTrigger data-testid={`map-${idx}`} className="bg-white h-10 flex-1"><SelectValue placeholder="— skip —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__skip__">— skip —</SelectItem>
                        {CANONICAL.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <div className="eyebrow mb-2">Sample rows</div>
                <div className="overflow-x-auto rounded-lg border border-[color:var(--border)]">
                  <table className="text-xs w-full">
                    <thead><tr className="bg-[color:var(--surface)]">{preview.headers.map((h, i) => <th key={i} className="text-left p-2 font-semibold">{h}</th>)}</tr></thead>
                    <tbody>{preview.sample_rows.slice(0, 5).map((r, i) => <tr key={i} className="border-t border-[color:var(--border)]">{r.map((c, j) => <td key={j} className="p-2 text-[color:var(--text-muted)]">{c}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-lg">Cancel</Button>
            <Button data-testid="confirm-mapping" onClick={confirmSpreadsheet} className="btn-sage rounded-lg">Import with this mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportBtn({ testid, accept, onFile, icon: Icon, label, multiple }) {
  return (
    <label className="cursor-pointer rounded-lg border border-[color:var(--border-strong)] bg-white p-4 flex flex-col items-center gap-2 hover:bg-[color:var(--surface)] transition-colors text-center">
      <input data-testid={testid} type="file" accept={accept} multiple={multiple} className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          files.forEach(f => onFile(f));
          e.target.value = "";
        }} />
      <Icon className="w-5 h-5" /><span className="text-xs font-semibold">{label}</span>
    </label>
  );
}

function ManualBuilder({ vendorId, vendorCategory, menus, onChange }) {
  const L = offeringLabels(vendorCategory);
  const [newMenuName, setNewMenuName] = useState("");
  const [newCatByMenu, setNewCatByMenu] = useState({});
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [item, setItem] = useState({ name: "", description: "", price: 0, tags: "", available: true, featured: false, image_url: "", pricing_type: "flat_rate", booking_mode: "quote" });
  const [editing, setEditing] = useState(null);
  const openEdit = (it) => {
    setEditing(it);
    setItem({
      name: it.name || "",
      description: it.description || "",
      price: it.price || 0,
      tags: Array.isArray(it.tags) ? it.tags.join(", ") : "",
      available: it.available !== false,
      featured: !!it.featured,
      image_url: (it.images && it.images[0]) || "",
      pricing_type: it.pricing_type || "flat_rate",
      booking_mode: it.booking_mode || "quote",
    });
    setActiveCategory({ category_id: it.category_id, name: "" });
    setItemDialogOpen(true);
  };

  const addMenu = async () => {
    if (!newMenuName.trim()) return;
    if (!vendorId) return toast.error("No vendor selected. Please refresh and try again.");
    try {
      await api.post("/menus", { vendor_id: vendorId, name: newMenuName.trim() });
      setNewMenuName("");
      toast.success(L.menuCreated);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create menu. Please try again.");
    }
  };
  const addCategory = async (menuId) => {
    const name = (newCatByMenu[menuId] || "").trim();
    if (!name) return;
    if (!menuId) return toast.error("Missing menu context. Please refresh.");
    try {
      await api.post("/menu-categories", { menu_id: menuId, name });
      setNewCatByMenu({ ...newCatByMenu, [menuId]: "" });
      toast.success(L.categoryAdded);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add category. Please try again.");
    }
  };
  const openItem = (cat) => { setEditing(null); setActiveCategory(cat); setItem({ name: "", description: "", price: 0, tags: "", available: true, featured: false, image_url: "", pricing_type: "flat_rate", booking_mode: "quote" }); setItemDialogOpen(true); };
  const saveItem = async () => {
    if (!item.name.trim()) return toast.error("Name is required");
    const forceQuote = item.pricing_type === "starting_at" || item.pricing_type === "contact";
    const bm = forceQuote ? "quote" : item.booking_mode;
    const payload = {
      name: item.name.trim(), description: item.description, price: +item.price,
      images: item.image_url ? [item.image_url] : [],
      tags: item.tags.split(",").map(s => s.trim()).filter(Boolean),
      available: item.available, featured: item.featured,
      pricing_type: item.pricing_type, booking_mode: bm,
    };
    try {
      if (editing) {
        await api.put(`/menu-items/${editing.item_id}`, payload);
        toast.success(L.itemUpdated);
      } else {
        if (!activeCategory?.category_id) return toast.error("Missing category context. Please close and reopen this form.");
        await api.post("/menu-items", { category_id: activeCategory.category_id, ...payload });
        toast.success(L.itemAdded);
      }
      setItemDialogOpen(false); setEditing(null); onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save item. Please try again.");
    }
  };
  const remove = async (kind, id) => {
    const url = kind === "menu" ? `/menus/${id}` : kind === "cat" ? `/menu-categories/${id}` : `/menu-items/${id}`;
    await api.delete(url); onChange();
  };

  return (
    <div className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <div className="eyebrow mb-1">Manual entry</div>
          <h2 className="text-2xl font-semibold" data-testid="builder-title">{L.builderTitle}</h2>
          {L.builderHint && <p className="text-sm text-[color:var(--text-muted)] mt-1" data-testid="builder-hint">{L.builderHint}</p>}
        </div>
        <div className="flex gap-2 w-full sm:w-auto sm:shrink-0">
          <Input data-testid="new-menu-name" placeholder={L.menuPlaceholder} value={newMenuName} onChange={e => setNewMenuName(e.target.value)} className="h-10 bg-white flex-1 min-w-0 sm:flex-none sm:w-56" />
          <Button data-testid="add-menu-btn" onClick={addMenu} disabled={!vendorId || !newMenuName.trim()} className="btn-sage rounded-lg h-10 whitespace-nowrap"><Plus className="w-4 h-4 mr-1" /> {L.addMenu}</Button>
        </div>
      </div>

      {menus.length === 0 ? (
        <p className="text-sm text-[color:var(--text-muted)]" data-testid="builder-empty">{L.emptyText}</p>
      ) : menus.map(m => (
        <div key={m.menu_id} data-testid={`menu-${m.menu_id}`} className="mt-6 border-t border-[color:var(--border)] pt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-semibold">{m.name}</div>
            <button onClick={() => remove("menu", m.menu_id)} data-testid={`delete-menu-${m.menu_id}`} className="text-[color:var(--accent)] text-xs">{L.deleteMenu}</button>
          </div>
          <div className="flex gap-2 mb-4">
            <Input data-testid={`new-cat-${m.menu_id}`} placeholder={L.categoryPlaceholder} value={newCatByMenu[m.menu_id] || ""} onChange={e => setNewCatByMenu({...newCatByMenu, [m.menu_id]: e.target.value})} className="h-9 bg-white max-w-xs" />
            <Button data-testid={`add-cat-${m.menu_id}`} variant="outline" onClick={() => addCategory(m.menu_id)} className="rounded-lg h-9 whitespace-nowrap">{L.addCategory}</Button>
          </div>
          {m.categories.map(c => (
            <div key={c.category_id} data-testid={`cat-${c.category_id}`} className="rounded-lg bg-[color:var(--surface)] p-4 mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">{c.name}</div>
                <div className="flex gap-2">
                  <Button data-testid={`add-item-${c.category_id}`} onClick={() => openItem(c)} variant="outline" className="rounded-lg h-8 text-xs bg-white whitespace-nowrap">{L.addItem}</Button>
                  <button onClick={() => remove("cat", c.category_id)} className="text-[color:var(--accent)] text-xs">Delete</button>
                </div>
              </div>
              {c.items.length === 0 ? (
                <div className="text-xs text-[color:var(--text-muted)]">No items yet.</div>
              ) : (
                <div className="space-y-2">
                  {c.items.map(it => (
                    <div key={it.item_id} className="bg-white rounded-lg border border-[color:var(--border)] p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{it.name}</span>
                          {it.featured && <Star className="w-3.5 h-3.5 fill-[#F5A524] text-[#F5A524]" />}
                          {!it.available && <span className="text-[10px] uppercase tracking-widest text-[color:var(--accent)]">Unavailable</span>}
                        </div>
                        <div className="text-xs text-[color:var(--text-muted)] truncate">{it.description}</div>
                      </div>
                      <div className="text-sm font-semibold">${it.price}</div>
                      <button onClick={() => openEdit(it)} data-testid={`edit-item-${it.item_id}`} className="text-[color:var(--text-muted)] hover:text-neutral-900 text-xs px-2">Edit</button>
                      <button onClick={() => remove("item", it.item_id)} className="text-[color:var(--accent)]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent data-testid="item-dialog" className="max-w-md bg-[color:var(--bg)]">
          <DialogHeader><DialogTitle className="text-2xl font-semibold" data-testid="item-dialog-title">{editing ? `Edit ${item.name}` : L.addItemTitle(activeCategory?.name)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="item-name" placeholder="Name" value={item.name} onChange={e => setItem({...item, name: e.target.value})} className="bg-white h-11" />
            <Textarea data-testid="item-description" placeholder="Description" value={item.description} onChange={e => setItem({...item, description: e.target.value})} rows={2} className="bg-white" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="item-pricing-type" value={item.pricing_type} onChange={e => { const pt = e.target.value; const forceQuote = pt === "starting_at" || pt === "contact"; setItem({...item, pricing_type: pt, booking_mode: forceQuote ? "quote" : item.booking_mode}); }} className="h-11 w-full rounded-lg border border-[color:var(--border-strong)] bg-white px-3 text-sm">
                <option value="per_person">Per person</option>
                <option value="per_hour">Per hour</option>
                <option value="per_item">Per item</option>
                <option value="flat_rate">Flat rate</option>
                <option value="starting_at">Starting at</option>
                <option value="contact">Contact for pricing</option>
              </select>
              <Input data-testid="item-price" type="number" placeholder={{per_person:"Price per person",per_hour:"Hourly rate",per_item:"Price per item",flat_rate:"Package price",starting_at:"Starting price",contact:"Price (optional)"}[item.pricing_type] || "Price"} value={item.price} onChange={e => setItem({...item, price: +e.target.value})} className="bg-white h-11" />
            </div>
            <div className="rounded-lg border border-[color:var(--border)] bg-white p-3 space-y-2" data-testid="item-booking-method">
              <div className="eyebrow">Booking method</div>
              {(() => { const forceQuote = item.pricing_type === "starting_at" || item.pricing_type === "contact"; return (<>
                <label className={`flex items-start gap-2 cursor-pointer ${forceQuote ? "opacity-50" : ""}`}>
                  <input type="radio" name="item_booking_mode" value="instant" checked={item.booking_mode === "instant"} disabled={forceQuote} onChange={() => !forceQuote && setItem({...item, booking_mode: "instant"})} data-testid="item-booking-mode-instant" className="mt-0.5" />
                  <div className="flex-1"><div className="text-sm font-semibold">Instant Booking</div><div className="text-[12px] text-[color:var(--text-muted)] leading-snug">Customers can book and pay the required deposit without waiting for your approval.</div></div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="item_booking_mode" value="quote" checked={item.booking_mode === "quote"} onChange={() => setItem({...item, booking_mode: "quote"})} data-testid="item-booking-mode-quote" className="mt-0.5" />
                  <div className="flex-1"><div className="text-sm font-semibold">Quote Required</div><div className="text-[12px] text-[color:var(--text-muted)] leading-snug">Customers request first; you send the final quote before payment.</div></div>
                </label>
                {forceQuote && <div className="text-[11px] text-[color:var(--text-muted)] italic pt-1">Instant Booking isn't available for "{item.pricing_type === "starting_at" ? "Starting at" : "Contact for pricing"}".</div>}
              </>); })()}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <Input data-testid="item-image" placeholder="Image URL (optional)" value={item.image_url} onChange={e => setItem({...item, image_url: e.target.value})} className="bg-white h-11" />
            </div>
            <Input data-testid="item-tags" placeholder="Tags (comma separated)" value={item.tags} onChange={e => setItem({...item, tags: e.target.value})} className="bg-white h-11" />
            <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="item-available" checked={item.available} onCheckedChange={(v) => setItem({...item, available: v === true})} /> Available</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="item-featured" checked={item.featured} onCheckedChange={(v) => setItem({...item, featured: v === true})} /> Featured</label>
          </div>
          <DialogFooter><Button data-testid="save-item" onClick={saveItem} className="btn-sage rounded-lg">{editing ? "Save changes" : L.addItemButton}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
